import { ExamCode } from "../../../../../../api/interfaces/preboard/ExamCode";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, Button, CircularProgress, Divider, IconButton, Typography } from "@mui/material";
import { NO_DATA_TO_DISPLAY_WITHOUT_TEXT } from "../../../../../../utils/constants";
import { ExamResponse } from "../../../../../../api/interfaces/preboard/Exam";

type ExamListViewProps = {
    examineeId: string;
    activatedExamCodes: ExamCode[];
    expandedExamId: string | null;
    loadingByCode: Record<string, boolean>;
    examsByCode: Record<string, any[]>;
    onToggle: (codeId: string) => void;
    onModify: (exam: ExamResponse) => void;
};

export const ExamListView: React.FC<ExamListViewProps> = ({
    activatedExamCodes,
    expandedExamId,
    loadingByCode,
    examsByCode,
    onToggle,
    onModify,
}) => {
    if (activatedExamCodes.length === 0) {
        return (
            <Box textAlign="center" py={4} color="#818181" sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
            }}>
                <Box
                    component="img"
                    src={NO_DATA_TO_DISPLAY_WITHOUT_TEXT}
                    sx={{ width: 200, mb: 1 }}
                />
                <Typography variant="caption">
                    No data to display
                </Typography>
            </Box>
        );
    }

    return (
        <>
            {activatedExamCodes.length === 0 ? (
                <Box
                    sx={{
                        py: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#818181",
                    }}
                >
                    <Box
                        component="img"
                        src={NO_DATA_TO_DISPLAY_WITHOUT_TEXT}
                        alt="No data"
                        sx={{
                            width: 200,
                            maxWidth: "100%",
                            objectFit: "contain",
                            mb: 1,
                        }}
                    />
                    <Typography variant="caption">
                        No data to display
                    </Typography>
                </Box>
            ) :
                activatedExamCodes.map((activatedExamCode: ExamCode) => (
                    <Box key={activatedExamCode._id}>
                        <Box
                            onClick={() => onToggle(activatedExamCode._id)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                py: 1.5,
                                cursor: "pointer",
                                "&:hover": {
                                    backgroundColor: "#f9f9f9",
                                },
                            }}
                        >
                            {/* Left Content */}
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: "#5840BA",
                                    }}
                                >
                                    {activatedExamCode.name}
                                </Typography>

                                <Box sx={{
                                    display: "flex",
                                    gap: 3
                                }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: "#818181", mt: 0.3 }}
                                    >
                                        No. of Exams: {activatedExamCode.numberOfExams}
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        sx={{ color: "#818181", mt: 0.3 }}
                                    >
                                        Exam Code: {activatedExamCode.code}
                                    </Typography>

                                </Box>
                            </Box>

                            {/* Right Action */}
                            <IconButton
                                color="primary"
                                onClick={() => onToggle(activatedExamCode._id)}
                            >
                                {expandedExamId === activatedExamCode._id ? (
                                    <RemoveCircleIcon />
                                ) : (
                                    <AddCircleIcon />
                                )}
                            </IconButton>
                        </Box>

                        <Divider />

                        {expandedExamId === activatedExamCode._id && (
                            <Box sx={{ pl: 3, py: 2 }}>
                                {loadingByCode[activatedExamCode._id] ? (
                                    <Box sx={{ textAlign: "center", py: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : examsByCode[activatedExamCode._id]?.length === 0 ? (
                                    <Box
                                        sx={{
                                            py: 3,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#818181",
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={NO_DATA_TO_DISPLAY_WITHOUT_TEXT}
                                            alt="No data"
                                            sx={{
                                                width: 200,
                                                maxWidth: "100%",
                                                objectFit: "contain",
                                                mb: 1,
                                            }}
                                        />
                                        <Typography variant="caption">
                                            No data to display
                                        </Typography>
                                    </Box>
                                ) : (
                                    examsByCode[activatedExamCode._id]?.map((exam: ExamResponse) => (
                                        <Box
                                            key={exam._id}
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                mb: 2,
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {exam.name}
                                                </Typography>

                                                <Typography variant="caption" sx={{ color: "#818181" }}>
                                                    Score / Items:{" "}
                                                    {exam.highestScore === null ? "TBA" : exam.highestScore} /{" "}
                                                    {exam.numberOfQuestions}
                                                </Typography>

                                                <br />

                                                <Typography variant="caption" sx={{ color: "#818181" }}>
                                                    Attempt: {exam.numberOfAttempts} of {exam.maxAttempts}
                                                </Typography>
                                            </Box>

                                            <Button
                                                size="small"
                                                variant="contained"
                                                disabled={exam.numberOfAttempts === 0}
                                                startIcon={<SettingsOutlinedIcon />}
                                                onClick={() => onModify(exam)}
                                                sx={{
                                                    fontSize: "12px",
                                                    alignSelf: "flex-end",
                                                    textTransform: "none",
                                                    borderRadius: 1,
                                                    minWidth: 80,
                                                    height: "24px",
                                                    backgroundColor: "#5840BA",
                                                    color: "#fff",
                                                    "&:hover": {
                                                        backgroundColor: "#4a35a8",
                                                    },
                                                    "&.Mui-disabled": {
                                                        backgroundColor: "#B7ACE3",
                                                        color: "#ffffff",
                                                        opacity: 1,
                                                    },
                                                    "&.Mui-disabled .MuiButton-startIcon": {
                                                        color: "#ffffff",
                                                    },
                                                }}
                                            >
                                                Modify
                                            </Button>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        )}
                    </Box>
                ))}
        </>
    );
};
